/**
 * ServiceStatusBanner — Wartungs-Hinweis bei Backend-Ausfall.
 *
 * Verhalten:
 *   • Pollt /api/health alle 30s
 *   • Nach 2 aufeinander folgenden Fehlern (Network/5xx/Timeout) → Banner zeigen
 *   • Nach 1 erfolgreichem Check → Banner ausblenden
 *   • Dismiss-Button blendet den Banner für 10 Min lokal aus (localStorage)
 *   • Manueller Override: localStorage `zozo_maintenance` = 'force' / 'off' / 'auto'
 *
 * Hardcoded Kontakt-Fallbacks (für den Fall, dass Locations gerade NICHT
 * geladen werden konnten — z.B. erster Page-Load während Backend down ist).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, AlertTriangle, X } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

const HEALTH_PATH = '/api/health';
const POLL_INTERVAL_MS = 30_000;
const FIRST_CHECK_DELAY_MS = 3_000;
const FAILURE_THRESHOLD = 2;
const DISMISS_DURATION_MS = 10 * 60 * 1000; // 10 Min
const DISMISS_KEY = 'zozo_status_banner_dismissed_until';
const OVERRIDE_KEY = 'zozo_maintenance';

// Hardcoded Kontakte – fallen ein, wenn Locations nicht geladen werden konnten
const FALLBACK_BRANCHES = [
  { name: 'Rellingen', phone: '04101 39 84 850' },
  { name: 'Henstedt-Ulzburg', phone: '04193 75 28 73' },
];

const isDismissed = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until > Date.now();
  } catch { return false; }
};

const setDismissed = () => {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS)); } catch { /* ignore */ }
};

const getOverride = () => {
  try { return localStorage.getItem(OVERRIDE_KEY) || 'auto'; } catch { return 'auto'; }
};

async function checkHealth() {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(`${API_BASE}${HEALTH_PATH}`, {
      method: 'GET',
      signal: ctrl.signal,
      cache: 'no-store',
    });
    clearTimeout(t);
    if (!res.ok) return false;
    // Backend can be "ok" or "degraded" — both are "reachable" enough for ordering
    return true;
  } catch {
    clearTimeout(t);
    return false;
  }
}

export default function ServiceStatusBanner({ branches = [] }) {
  const [show, setShow] = useState(false);
  const failuresRef = useRef(0);
  const mountedRef = useRef(true);

  const runCheck = useCallback(async () => {
    const override = getOverride();
    if (override === 'force') { setShow(true); return; }
    if (override === 'off')   { setShow(false); return; }
    // auto mode
    if (isDismissed()) { setShow(false); return; }
    const ok = await checkHealth();
    if (!mountedRef.current) return;
    if (ok) {
      failuresRef.current = 0;
      setShow(false);
    } else {
      failuresRef.current += 1;
      if (failuresRef.current >= FAILURE_THRESHOLD) setShow(true);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Trigger ersten Check leicht verzögert, damit Boot-Phase nicht stört
    const first = setTimeout(runCheck, FIRST_CHECK_DELAY_MS);
    const iv = setInterval(runCheck, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearTimeout(first);
      clearInterval(iv);
    };
  }, [runCheck]);

  if (!show) return null;

  const list = (branches && branches.length > 0
    ? branches
        .filter(b => b?.phone && (b?.is_active !== false))
        .map(b => ({ name: b.name?.replace(/^ZOZO Burger\s*/i, '') || b.slug || 'Filiale', phone: b.phone }))
    : FALLBACK_BRANCHES);

  return (
    <div
      data-testid="service-status-banner"
      role="alert"
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-amber-500 to-amber-600 text-stone-900 shadow-lg"
      style={{ animation: 'slideDown 0.4s ease-out' }}
    >
      <style>{`@keyframes slideDown { from { transform: translateY(-100%);} to { transform: translateY(0);} }`}</style>
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm leading-tight">
              Wir sind kurz offline — Online-Bestellungen aktuell nicht möglich
            </p>
            <p className="text-xs sm:text-sm opacity-90 mt-0.5">
              Bitte ruf uns direkt an, wir nehmen deine Bestellung gerne telefonisch entgegen:
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
              {list.map(b => (
                <a
                  key={b.name}
                  href={`tel:${b.phone.replace(/\s+/g, '')}`}
                  className="inline-flex items-center gap-1.5 font-semibold text-sm hover:underline"
                  data-testid={`status-banner-tel-${(b.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span className="whitespace-nowrap">{b.name}: {b.phone}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setDismissed(); setShow(false); }}
          aria-label="Hinweis ausblenden"
          className="flex-shrink-0 p-1.5 rounded-full hover:bg-black/10 transition"
          data-testid="status-banner-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
